import { AfterViewInit, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { AppState } from 'src/app/core/app.state';
import { FeatureFlagService } from 'src/app/core/services/feature-flag.service';
import {
  ICountry, ICurrency, ILocation, IMainContact, IPayment_term, IState
} from 'src/app/features/customers/models/customers.models';
import { FetchAllCountries, FetchAllStates } from 'src/app/features/customers/store/customers.actions';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { AddContactsComponent, AddContactDialogData, AddContactDialogResult } from '../add-contacts/add-contacts.component';

declare var google: any;

export interface AddLocationDialogData {
  currentLocation?: ILocation;
  currencies: ICurrency[];
  paymentTerms: IPayment_term[];
  contacts: IMainContact[];
  isMainLocationExists: boolean;
  allLocations?: ILocation[];
}

/** Create/edit location dialog; address parts live outside the form as store-backed refs. */
@Component({
  selector: 'app-add-locations',
  templateUrl: './add-locations.component.html',
  styleUrls: ['./add-locations.component.scss']
})
export class AddLocationsComponent implements OnInit, AfterViewInit, OnDestroy {

  form!: FormGroup;
  isEditMode = false;
  availableContacts: IMainContact[] = [];
  selectedContactEmail = '';

  countries: ICountry[] = [];
  states: IState[] = [];

  country!: ICountry;
  state!: IState;
  city = '';
  zip = '';
  billing_country!: ICountry;
  billing_state!: IState;
  billing_city = '';
  billing_zip = '';
  lang = '';

  private subscriptions: Subscription[] = [];

  constructor(
    public dialogRef: MatDialogRef<AddLocationsComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AddLocationDialogData,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private store: Store<AppState>,
    private translateService: TranslateService,
    public featureFlagService: FeatureFlagService,
  ) {
    this.store.dispatch(FetchAllCountries());
    this.store.dispatch(FetchAllStates());
  }

  ngOnInit(): void {
    this.availableContacts = this.data.currentLocation ? [...(this.data.contacts || [])] : [];

    const subCountries = this.store
      .select(state => state.customers.allCountries)
      .subscribe(c => this.countries = c);
    const subStates = this.store
      .select(state => state.customers.allStates)
      .subscribe(s => this.states = s);
    this.subscriptions.push(subCountries, subStates);

    if (this.data.currentLocation) {
      this.isEditMode = true;
      this.initForm(this.data.currentLocation);
      this.country = this.data.currentLocation.country as ICountry;
      this.state = this.data.currentLocation.state as IState;
      this.city = this.data.currentLocation.city || '';
      this.zip = this.data.currentLocation.zip || '';
      this.billing_country = this.data.currentLocation.billing_country as ICountry;
      this.billing_state = this.data.currentLocation.billing_state as IState;
      this.billing_city = this.data.currentLocation.billing_city || '';
      this.billing_zip = this.data.currentLocation.billing_zip || '';
      this.lang = this.data.currentLocation.lang || '';

      if (this.data.currentLocation.contacts_information?.length) {
        const mainContact = this.data.currentLocation.contacts_information.find(c => c.is_user);
        this.selectedContactEmail =
          mainContact?.email ?? this.data.currentLocation.contacts_information[0].email;
      }
    } else {
      this.initForm();
    }
  }

  ngAfterViewInit(): void {
    // Defer until dialog inputs exist; Places listener is not removed on destroy.
    setTimeout(() => {
      this.addressAutoComplete('loc-apartment');
      this.addressAutoComplete('loc-billing-apartment');
    }, 300);
  }

  private initForm(location?: ILocation): void {
    this.form = this.fb.group({
      name: new FormControl(location?.name ?? '', Validators.required),
      tax_id: new FormControl(location?.tax_id ?? '', Validators.maxLength(64)),
      erp_id: new FormControl(location?.company_id ?? '', Validators.maxLength(64)),
      apartment: new FormControl(location?.apartment ?? ''),
      billing_apartment: new FormControl(location?.billing_apartment ?? ''),
      is_bill_same: new FormControl(location?.is_bill_same ?? false),
      currency: new FormControl(
        location?.currency?.id != null ? String(location.currency.id) : ''
      ),
      payment_terms: new FormControl(
        location?.payment_terms?.id != null ? String(location.payment_terms.id) : ''
      ),
      is_main: new FormControl(location?.is_main ?? false),
      tax: new FormControl(location?.tax ?? ''),
      custom_discount: new FormControl(location?.custom_discount ?? ''),
    });
  }

  /** Attach Google Places autocomplete; mirrors billing when `is_bill_same` is checked. */
  addressAutoComplete(elementId: string): void {
    const input = document.getElementById(elementId) as HTMLInputElement;
    if (!input || typeof google === 'undefined') return;

    const autocomplete = new google.maps.places.Autocomplete(input, { strictBounds: false });
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place) return;

      let countryName = '';
      let stateName = '';
      let cityName = '';
      let zipCode = '';
      let short_name = '';

      place.address_components?.forEach((component: any) => {
        if (component.types.includes('country')) {
          countryName = component.long_name;
          short_name = component.short_name;
        }
        if (component.types.includes('administrative_area_level_1')) stateName = component.long_name;
        if (component.types.includes('administrative_area_level_2')) cityName = component.long_name;
        if (component.types.includes('postal_code')) zipCode = component.long_name;
      });

      if (elementId === 'loc-apartment') {
        this.lang = short_name === 'IT' ? 'it_IT' : short_name === 'FR' ? 'fr_FR' : short_name === 'US' ? 'en_US' : 'it_IT';
        this.form.get('apartment')?.patchValue(place.formatted_address);
        this.country = this.countries.find(c => c.name === countryName)!;
        this.state = this.states.find(s => s.name === stateName)!;
        this.city = cityName;
        this.zip = zipCode;
        if (this.form.get('is_bill_same')?.value) {
          this.form.get('billing_apartment')?.setValue(place.formatted_address);
          this.billing_country = this.country;
          this.billing_state = this.state;
          this.billing_city = cityName;
          this.billing_zip = zipCode;
        }
      }
      if (elementId === 'loc-billing-apartment' && !this.form.get('is_bill_same')?.value) {
        this.form.get('billing_apartment')?.patchValue(place.formatted_address);
        this.billing_country = this.countries.find(c => c.name === countryName)!;
        this.billing_state = this.states.find(s => s.name === stateName)!;
        this.billing_city = cityName;
        this.billing_zip = zipCode;
      }
    });
  }

  /** Snapshot-copy shipping to billing; not a live mirror. */
  toggleBillingAddress(): void {
    const sameAsShipping = this.form.get('is_bill_same')?.value;
    if (sameAsShipping) {
      this.form.patchValue({
        billing_apartment: this.form.get('apartment')?.value,
      });
      this.billing_country = this.country;
      this.billing_state = this.state;
      this.billing_city = this.city;
      this.billing_zip = this.zip;
    } else {
      this.form.patchValue({ billing_apartment: '' });
    }
  }

  onContactSelected(event: any): void {
    this.selectedContactEmail = event.target.value;
  }

  /** Confirm before promoting a location when another main already exists. */
  onMainToggleChange(event: MatSlideToggleChange): void {
    const wasAlreadyMain = !!this.data.currentLocation?.is_main;
    if (event.checked && this.data.isMainLocationExists && !wasAlreadyMain) {
      const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
        data: {
          title: this.translateService.instant('customers.add_location.replace_main_dialog.title'),
          text: this.translateService.instant('customers.add_location.replace_main_dialog.text'),
          confirm_text: this.translateService.instant('customers.add_location.replace_main_dialog.confirm'),
          cancel_text: this.translateService.instant('customers.add_location.replace_main_dialog.cancel'),
        },
        disableClose: true,
      });
      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        if (!confirmed) {
          this.form.get('is_main')?.setValue(false, { emitEvent: false });
        }
      });
    }
  }

  createContact(): void {
    const parentLocationId = this.data.currentLocation?.id;
    const dialogRef = this.dialog.open(AddContactsComponent, {
      data: parentLocationId != null
        ? {
            contacts_information: this.availableContacts,
            locationId: parentLocationId,
          } as AddContactDialogData
        : {
            contacts_information: this.availableContacts,
            bufferOnly: true,
          } as AddContactDialogData,
      disableClose: true,
      panelClass: 'ac-dialog-panel',
    });
    dialogRef.afterClosed().subscribe((result: AddContactDialogResult) => {
      if (result?.newContact) {
        const c = result.newContact;
        const asContact: IMainContact = {
          id: c.id,
          contact_fname: c.contact_fname,
          contact_lname: c.contact_lname ?? '',
          email: c.email,
          phone: c.phone ?? '',
          receive_emails: c.receive_emails,
          receive_sms: c.receive_sms,
          is_user: c.is_user,
          lang: c.lang,
          signature: c.signature ?? '',
          create_date: new Date().toISOString(),
        };
        this.availableContacts = [...this.availableContacts, asContact];
        this.selectedContactEmail = asContact.email;
      }
    });
  }

  proceed(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const formValue = this.form.getRawValue();

    let contacts: IMainContact[] = [];

    // WB-7370: the Main Contact Section (and its is_user reassignment) is hidden from the UI
    // when the flag is off, so selectedContactEmail/availableContacts can never be
    // user-modified in that state — pass existing contacts through unchanged instead of running
    // the reassignment logic below.
    if (!this.featureFlagService.traditionalContactsEnabled) {
      contacts = this.isEditMode ? [...(this.data.currentLocation?.contacts_information ?? [])] : [];
    } else if (this.isEditMode) {
      const existing = this.data.currentLocation?.contacts_information ?? [];
      if (this.selectedContactEmail) {
        const sel = this.availableContacts.find(c => c.email === this.selectedContactEmail);
        if (sel) {
          contacts = [
            { ...sel, is_user: true },
            ...existing
              .filter(c => c.email !== this.selectedContactEmail)
              .map(c => ({ ...c, is_user: false })),
          ];
        } else {
          contacts = [...existing];
        }
      } else {
        contacts = [...existing];
      }
    } else {
      contacts = this.availableContacts.map((c, i) => ({ ...c, is_user: i === 0 }));
    }

    this.dialogRef.close({
      ...formValue,
      country: this.country,
      state: this.state,
      city: this.city,
      zip: this.zip,
      billing_country: this.billing_country,
      billing_state: this.billing_state,
      billing_city: this.billing_city,
      billing_zip: this.billing_zip,
      lang: this.lang,
      contacts_information: contacts,
    });
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
